import type { NitroFetchRequest, $Fetch } from 'nitropack'
import type { FetchOptions } from 'ofetch'
import type { CreateBillingAddressRequest } from '~/types/apis/createBillingAddress'
import type { CreateShippingAddressRequest } from '~/types/apis/createShippingAddress'
import type { GetUserResponse } from '~/types/apis/getUser'
import type {
  RequestChangePhoneNumberOtpRequest,
  RequestChangePhoneNumberOtpResponse
} from '~/types/apis/requestChangePhoneNumberOtp'
import type { UpdateBillingAddressRequest } from '~/types/apis/updateBillingAddress'
import type { UpdateShippingAddressRequest } from '~/types/apis/updateShippingAddress'
import type {
  UpdateUserRequest,
  UpdateUserResponse
} from '~/types/apis/updateUser'
import type {
  VerifyChangePhoneNumberOtpRequest,
  VerifyChangePhoneNumberOtpResponse
} from '~/types/apis/verifyChangePhoneNumberOtp'

export const userRepository = <T>(fetch: $Fetch<T, NitroFetchRequest>) => ({
  async getUser(options: FetchOptions = {}): Promise<GetUserResponse> {
    return fetch<GetUserResponse>('/v1/storefront/customer/detail', {
      ...options,
      method: 'GET'
    })
  },
  async requestChangePhoneNumberOtp(
    token?: string,
    options: FetchOptions & { body?: RequestChangePhoneNumberOtpRequest } = {}
  ): Promise<RequestChangePhoneNumberOtpResponse> {
    return fetch<RequestChangePhoneNumberOtpResponse>(
      '/v1/storefront/customer/edit/change-phone',
      {
        ...options,
        headers: {
          ...options.headers,
          ...(token && { Authorization: `Bearer ${token}` })
        },
        method: 'POST'
      }
    )
  },
  async verifyChangePhoneNumberOtp(
    options: FetchOptions & { body?: VerifyChangePhoneNumberOtpRequest } = {}
  ): Promise<VerifyChangePhoneNumberOtpResponse> {
    return fetch<VerifyChangePhoneNumberOtpResponse>(
      '/v1/storefront/customer/edit/change-phone/verify',
      {
        ...options,
        method: 'POST'
      }
    )
  },
  async updateUser(
    options: FetchOptions & { body?: UpdateUserRequest } = {}
  ): Promise<UpdateUserResponse> {
    return fetch<UpdateUserResponse>('/v1/storefront/customer/update', {
      ...options,
      method: 'PUT'
    })
  },
  async createShippingAddress(
    options: FetchOptions & { body?: CreateShippingAddressRequest } = {}
  ): Promise<CreateShippingAddressResponse> {
    return fetch<CreateShippingAddressResponse>(
      '/v1/storefront/customer/address/create',
      {
        ...options,
        method: 'POST'
      }
    )
  },
  async updateShippingAddress(
    addressId: string,
    options: FetchOptions & { body?: UpdateShippingAddressRequest } = {}
  ): Promise<UpdateShippingAddressResponse> {
    return fetch<UpdateShippingAddressResponse>(
      `/v1/storefront/customer/address/update/${addressId}`,
      {
        ...options,
        method: 'PUT'
      }
    )
  },
  async deleteShippingAddress(
    addressId: string,
    options: FetchOptions = {}
  ): Promise<DeleteShippingAddressResponse> {
    return fetch<DeleteShippingAddressResponse>(
      `/v1/storefront/customer/address/delete/${addressId}`,
      {
        ...options,
        method: 'DELETE'
      }
    )
  },
  async createBillingAddress(
    options: FetchOptions & { body?: CreateBillingAddressRequest } = {}
  ): Promise<CreateBillingAddressResponse> {
    return fetch<CreateBillingAddressResponse>(
      '/v1/storefront/customer/billing-address/create',
      {
        ...options,
        method: 'POST'
      }
    )
  },
  async updateBillingAddress(
    addressId: string,
    options: FetchOptions & { body?: UpdateBillingAddressRequest } = {}
  ): Promise<UpdateBillingAddressResponse> {
    return fetch<UpdateBillingAddressResponse>(
      `/v1/storefront/customer/billing-address/update/${addressId}`,
      {
        ...options,
        method: 'PUT'
      }
    )
  },
  async deleteBillingAddress(
    addressId: string,
    options: FetchOptions = {}
  ): Promise<DeleteBillingAddressResponse> {
    return fetch<DeleteBillingAddressResponse>(
      `/v1/storefront/customer/billing-address/delete/${addressId}`,
      {
        ...options,
        method: 'DELETE'
      }
    )
  },
  async getWishList(
    options: FetchOptions & {
      params?: {
        sort_by: string // available, out_of_stock, all
        per_page: number
      }
    } = {}
  ): Promise<GetWishListResponse> {
    return fetch<GetWishListResponse>('/v1/storefront/customer/wishlist', {
      ...options,
      method: 'GET'
    })
  }
})
